import React from 'react';
import { Type } from 'lucide-react';
import type { NavbarData, NavbarLogo, Page, Component } from '../ComponentEditModal.types';
import { LogoUploadModal } from '../../LogoUploadModal';
import {
  LogoPreview,
  NoLogoSection,
  NavigationLinksSection,
  AlignmentSection,
} from './NavbarSubComponents';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import {
  applyLegacyBothNavbarMigration,
  isLegacyBothLogo,
  markLegacyBothNoticeSeen,
} from '../../../utils/navbarLogo';

interface NavbarEditorProps {
  formData: NavbarData;
  setFormData: (data: NavbarData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  siteId?: string;
  isLoading?: boolean;
  isLogoModalOpen: boolean;
  setIsLogoModalOpen: (open: boolean) => void;
  currentPageComponents?: Component[];
}

export const NavbarEditor: React.FC<NavbarEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  siteId,
  isLoading = false,
  isLogoModalOpen,
  setIsLogoModalOpen,
  currentPageComponents,
}) => {
  const data = formData;
  const themeColors = useCustomColors();

  const handleLogoSave = (logoData: NavbarLogo) => {
    const fromLegacyBoth = isLegacyBothLogo(data.logo);
    const legacyText = data.logo?.text;

    let next: NavbarData = { ...data, logo: logoData };
    if (fromLegacyBoth) {
      next = applyLegacyBothNavbarMigration(next, logoData, legacyText);
      if (siteId) markLegacyBothNoticeSeen(siteId);
    }

    setFormData(next);
    setIsLogoModalOpen(false);
  };

  const removeLogo = () => {
    setFormData({ ...data, logo: undefined });
  };

  const hasLogo = data.logo && (data.logo.text || data.logo.imageUrl || data.logo.cloudfrontUrl);

  const addLink = () => {
    const newLink = {
      text: 'New Link',
      url: '/',
      order: (data.links || []).length,
      style: 'normal' as const,
    };
    setFormData({
      ...data,
      links: [...(data.links || []), newLink],
    });
  };

  const updateLink = (index: number, updates: Partial<NavbarData['links'][0]>) => {
    const newLinks = [...(data.links || [])];
    newLinks[index] = {
      ...newLinks[index],
      ...updates,
      order: updates.order !== undefined ? updates.order : (newLinks[index].order ?? index),
    };
    setFormData({ ...data, links: newLinks });
  };

  const removeLink = (index: number) => {
    const newLinks = (data.links || []).filter((_, i) => i !== index);
    const reorderedLinks = newLinks.map((link, i) => ({
      ...link,
      order: link.order ?? i,
    }));
    setFormData({ ...data, links: reorderedLinks });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('navbar', themeColors);

  return (
    <div className="space-y-6">
      {/* Brand/Logo Section */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <h3 className="font-medium text-gray-900 flex items-center space-x-2">
          <Type size={18} />
          <span>Brand & Logo</span>
        </h3>

        {hasLogo ? (
          <LogoPreview
            data={data}
            onEdit={() => setIsLogoModalOpen(true)}
            onRemove={removeLogo}
            onSiteNameChange={value => setFormData({ ...data, siteName: value })}
          />
        ) : (
          <NoLogoSection
            siteName={data.siteName}
            onSiteNameChange={value => setFormData({ ...data, siteName: value })}
            onAddLogo={() => setIsLogoModalOpen(true)}
          />
        )}
      </div>

      {/* Navigation Links */}
      <NavigationLinksSection
        links={data.links || []}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        onAddLink={addLink}
        onUpdateLink={updateLink}
        onRemoveLink={removeLink}
      />

      {/* Alignment and Sticky */}
      <AlignmentSection
        alignment={data.alignment}
        sticky={data.sticky}
        onAlignmentChange={value =>
          setFormData({
            ...data,
            alignment: value as 'left' | 'center' | 'right' | 'space-between',
          })
        }
        onStickyChange={value => setFormData({ ...data, sticky: value })}
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize navbar colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | null | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
      />

      {/* Logo Upload Modal */}
      <LogoUploadModal
        isOpen={isLogoModalOpen}
        onClose={() => setIsLogoModalOpen(false)}
        onSave={handleLogoSave}
        siteId={siteId || ''}
        initialData={data.logo}
        isLoading={isLoading}
        pages={pages}
        isLoadingPages={isLoadingPages}
      />
    </div>
  );
};
